import React, { useMemo, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const CELL_SIZE = 11;
const CELL_GAP = 3;
const CELL_STEP = CELL_SIZE + CELL_GAP;
const LEVEL_OPACITY = [1, 0.35, 0.55, 0.75, 1];

const monthNamesShort = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const weekdayNamesShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const dayMs = 24 * 60 * 60 * 1000;

function formatDayLabel(dayKey) {
  const [year, month, day] = dayKey.split("-").map(Number);
  return `${monthNamesShort[month - 1]} ${day}, ${year}`;
}

function buildYearWeeks(year, dailyCounts) {
  const start = new Date(Date.UTC(year, 0, 1));
  const end = new Date(Date.UTC(year, 11, 31));
  const gridStart = new Date(start.getTime() - start.getUTCDay() * dayMs);

  const weeks = [];
  let cur = gridStart;
  while (cur <= end) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const inYear = cur.getUTCFullYear() === year;
      const key = cur.toISOString().slice(0, 10);
      week.push({ date: key, count: inYear ? dailyCounts[key] || 0 : null });
      cur = new Date(cur.getTime() + dayMs);
    }
    weeks.push(week);
  }
  return weeks;
}

function levelFor(count, maxCount) {
  if (!count) return 0;
  if (maxCount <= 1) return 4;
  const ratio = count / maxCount;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

const Wrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;

const YearButton = styled.button`
  background: ${({ theme, active }) => (active ? theme.paginationBtn : "transparent")};
  color: ${({ theme, active }) => (active ? theme.card : theme.text)};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: 4px;
  padding: 0.15rem 0.6rem;
  font-size: 0.8rem;
  margin-left: 0.3rem;
  cursor: pointer;
`;

const ScrollArea = styled.div`
  overflow-x: auto;
  padding-bottom: 0.25rem;
`;

const GraphLayout = styled.div`
  display: inline-flex;
`;

const WeekdayColumn = styled.div`
  display: grid;
  grid-template-rows: repeat(7, ${CELL_SIZE}px);
  gap: ${CELL_GAP}px;
  margin-right: 4px;
  margin-top: ${CELL_STEP + 2}px;
`;

const WeekdayLabel = styled.div`
  font-size: 0.65rem;
  opacity: 0.6;
  height: ${CELL_SIZE}px;
  line-height: ${CELL_SIZE}px;
`;

const MonthRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: ${CELL_STEP}px;
  height: ${CELL_STEP}px;
  margin-bottom: 2px;
`;

const MonthLabel = styled.div`
  font-size: 0.65rem;
  opacity: 0.6;
  grid-column-start: ${({ $col }) => $col + 1};
`;

const Grid = styled.div`
  display: grid;
  grid-template-rows: repeat(7, ${CELL_SIZE}px);
  grid-auto-flow: column;
  grid-auto-columns: ${CELL_SIZE}px;
  gap: ${CELL_GAP}px;
`;

const Cell = styled.div`
  width: ${CELL_SIZE}px;
  height: ${CELL_SIZE}px;
  border-radius: 2px;
  visibility: ${({ $level }) => ($level === null ? "hidden" : "visible")};
  background-color: ${({ theme, $level }) => ($level === 0 ? theme.body : theme.paginationBtn)};
  opacity: ${({ $level }) => LEVEL_OPACITY[$level] ?? 1};
  border: ${({ theme, $level }) => ($level === 0 ? theme.cardBorder : "none")};
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.7rem;
  opacity: 0.7;
  margin-top: 0.4rem;
  gap: 3px;
`;

const ContributionGraph = ({ dailyCounts, entriesPerYear }) => {
  const availableYears = useMemo(() => {
    const years = (entriesPerYear || []).map(y => y.year);
    const currentYear = new Date().getUTCFullYear();
    if (!years.includes(currentYear)) years.push(currentYear);
    return years.sort((a, b) => a - b);
  }, [entriesPerYear]);

  const [selectedYear, setSelectedYear] = useState(
    availableYears[availableYears.length - 1]
  );

  const weeks = useMemo(
    () => buildYearWeeks(selectedYear, dailyCounts || {}),
    [selectedYear, dailyCounts]
  );

  const maxCount = useMemo(
    () => Math.max(1, ...weeks.flat().map(d => d.count || 0)),
    [weeks]
  );

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((week, i) => {
      const validDay = week.find(d => d.count !== null);
      if (!validDay) return;
      const month = Number(validDay.date.split("-")[1]) - 1;
      if (month !== lastMonth) {
        labels.push({ col: i, label: monthNamesShort[month] });
        lastMonth = month;
      }
    });
    return labels;
  }, [weeks]);

  return (
    <Wrapper>
      <Header>
        <h5 className="mb-0">Journaling activity</h5>
        <div>
          {availableYears.map(year => (
            <YearButton
              key={year}
              active={year === selectedYear}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </YearButton>
          ))}
        </div>
      </Header>
      <ScrollArea>
        <GraphLayout>
          <WeekdayColumn>
            {weekdayNamesShort.map((name, i) => (
              <WeekdayLabel key={name}>{i % 2 === 1 ? name : ""}</WeekdayLabel>
            ))}
          </WeekdayColumn>
          <div>
            <MonthRow>
              {monthLabels.map(m => (
                <MonthLabel key={m.col} $col={m.col}>
                  {m.label}
                </MonthLabel>
              ))}
            </MonthRow>
            <Grid>
              {weeks.flatMap((week, wi) =>
                week.map((day, di) => (
                  <Cell
                    key={`${wi}-${di}`}
                    $level={day.count === null ? null : levelFor(day.count, maxCount)}
                    title={
                      day.count === null
                        ? undefined
                        : `${day.count} entr${day.count === 1 ? "y" : "ies"} on ${formatDayLabel(day.date)}`
                    }
                  />
                ))
              )}
            </Grid>
          </div>
        </GraphLayout>
      </ScrollArea>
      <Legend>
        Less
        {[0, 1, 2, 3, 4].map(level => (
          <Cell key={level} $level={level} />
        ))}
        More
      </Legend>
    </Wrapper>
  );
};

ContributionGraph.propTypes = {
  dailyCounts: PropTypes.object,
  entriesPerYear: PropTypes.array,
};

export default ContributionGraph;
